import { IUser } from '@aca-new/app/pages/user/shared/models/interfaces/user.interface';
import { EAppcuesEvent } from '@aca-new/app/shared/models/enums/appcues-event.enum';
import { EAppcuesType } from '@aca-new/app/shared/models/enums/appcues-type.enum';
import { EBusinessUnit } from '@aca-new/app/shared/models/enums/business-unit.enum';
import { IAppcuesEvent } from '@aca-new/app/shared/models/interfaces/appcues-event.interface';
import { AcaMessageService } from '@aca-new/app/shared/services/post-message-services/aca-message/aca-message.service';
import { ScriptLoaderService } from '@aca-new/app/shared/services/third-party-services/script-loader/script-loader.service';
import { AppAuthenticationService } from '@aca-new/app/shared/services/user-services/app-authentication/app-authentication.service';
import { AppUserSettingService } from '@aca-new/app/shared/services/user-services/app-user-setting/app-user-setting.service';
import { APPCUES_ACCOUNT_ID } from '@aca-new/app/shared/tokens/appcues-account-id.token';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { QimaLoggerService, QimaOptionalType } from '@qima/ngx-qima';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

interface IBusinessProperties {
  isFoodCertMenu: boolean;
  isMyQimaCPUser: boolean;
  isMyQimaLSUser: boolean;
  isMyQimaProduceUser: boolean;
}

interface IAppcuesUserProperties extends Partial<IUser>, IBusinessProperties {
  isPaymentPortalUser?: boolean;
  selectedLanguage: string;
}

interface IAppcuesSdk {
  identify(userId: string, properties: Record<string, unknown>): void;
  on(eventName: string, callback: (name: string, event: IAppcuesEvent) => void): void;
  show?(contentId: string): void;
}

interface IAppcuesWindow {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Appcues SDK global on window
  Appcues?: IAppcuesSdk;
}

@Injectable({
  providedIn: 'root',
})
export class AppcuesService extends ScriptLoaderService {
  private readonly _userDataSentToAppcues: readonly string[] = [
    'firstName',
    'lastName',
    'login',
    'email',
    'sic',
    'sicId',
    'companyName',
    'companyId',
    'companyType',
    'companyHierarchyType',
    'newMyQimaUser',
  ];

  private readonly _lifeScienceBusinessUnits: readonly EBusinessUnit[] = [EBusinessUnit.MONASTERIUM, EBusinessUnit.NEWTONE, EBusinessUnit.BIOA];

  private readonly _id: QimaOptionalType<string>;
  private readonly _bannerVisibilityBehaviorSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _hasAppcuesScriptLoaded: boolean = false;
  private _hasBannerEventsRegistered: boolean = false;
  private _selectedLanguage: string | null = null;
  private _pendingIdentifyTraits: Record<string, unknown> = {};

  public constructor(
    private readonly _appAuthenticationService: AppAuthenticationService,
    private readonly _appUserSettingService: AppUserSettingService,
    private readonly _acaMessageService: AcaMessageService,
    private readonly _qimaLoggerService: QimaLoggerService,
    private readonly _translateService: TranslateService,
    @Inject(DOCUMENT) private readonly _document: Document,
    @Inject(APPCUES_ACCOUNT_ID) private readonly _appcuesId: Readonly<string>
  ) {
    super();
    this._id = this._appcuesId;

    if (this._id && !isNaN(parseInt(this._id, 10))) {
      this._data = {
        src: `//fast.appcues.com/${this._id}.js`,
      };
    }
  }

  public appcuesEvent$(): Observable<boolean> {
    return this._bannerVisibilityBehaviorSubject$.asObservable();
  }

  public toggleBanner(isVisible: boolean): void {
    this._bannerVisibilityBehaviorSubject$.next(isVisible);
  }

  /**
   * @description
   * Opens an AppCues experience by content id when the script is loaded.
   * @param {string} contentId - AppCues flow or survey content id.
   * @returns {void} Nothing.
   */
  public showContent(contentId: string): void {
    const appCues = this._getAppcues();

    if (!appCues) {
      return;
    }

    if (typeof appCues?.show !== 'function') {
      this._qimaLoggerService.debug('Appcues.show is unavailable', { contentId });

      return;
    }

    appCues.show(contentId);
  }

  /**
   * @description
   * Updates the current selected language on the AppCues user profile.
   * @param {string} language - Active translate locale (same codes as the language selector).
   * @returns {void} Nothing.
   */
  public syncSelectedLanguage(language: string): void {
    this._selectedLanguage = language;

    if (!this._hasAppcuesScriptLoaded) {
      return;
    }

    const userData = this._appAuthenticationService.getCurrentUser();

    if (!userData.id) {
      return;
    }

    this.identifySession({ selectedLanguage: language });
  }

  /**
   * @description
   * Identifies the Appcues profile using authenticated user traits or Anonymous.
   * @param {Record<string, unknown>} [extraTraits] - Optional caller-specific traits merged on top.
   * @returns {void} Nothing.
   */
  public identifySession(extraTraits: Record<string, unknown> = {}): void {
    const identifyTraits = {
      ...this._pendingIdentifyTraits,
      ...extraTraits,
    };

    if (!this._hasAppcuesScriptLoaded) {
      this._pendingIdentifyTraits = identifyTraits;

      return;
    }

    this._pendingIdentifyTraits = {};

    const appCues = this._getAppcues();

    if (!appCues) {
      this._pendingIdentifyTraits = identifyTraits;

      return;
    }

    const userData = this._appAuthenticationService.getCurrentUser();
    const identifyId = userData?.id ?? 'Anonymous';
    const identifyProperties: Record<string, unknown> = {
      ...this._buildUserProperties(userData),
      ...identifyTraits,
    };

    try {
      appCues.identify(identifyId, identifyProperties);
    } catch (error) {
      this._qimaLoggerService.debug('unable to identify Appcues session', error);
    }
  }

  /**
   * @description
   * Loads the Appcues script once, registers banner listeners, and identifies logged-in users.
   * Anonymous sessions defer identify to feature callers when they have context
   * (for example payment portal tracking after order data is loaded).
   * @returns {Observable<void>} Observable that completes when load setup finishes or the script was already loaded.
   */
  public appcuesLoader$(): Observable<void> {
    if (!this._data.src || this._hasAppcuesScriptLoaded) {
      return of(undefined);
    }

    return from(this._loadScript()).pipe(
      switchMap((): Observable<void> => {
        this._hasAppcuesScriptLoaded = true;
        this._registerBannerEvents();

        if (this._appAuthenticationService.getCurrentUser()?.id || Object.keys(this._pendingIdentifyTraits).length > 0) {
          this.identifySession();
        }

        return of(void 0);
      }),
      catchError((err): Observable<void> => {
        console.error('unable to load Appcues', err);
        this._qimaLoggerService.debug('unable to load Appcues', err);

        return of(void 0);
      })
    );
  }

  /**
   * @returns {IAppcuesSdk | undefined} Appcues SDK instance when the script is available on window.
   */
  private _getAppcues(): IAppcuesSdk | undefined {
    const windowRef = this._document.defaultView as (Window & IAppcuesWindow) | null;

    return windowRef?.Appcues;
  }

  /**
   * @param {Partial<IUser>} userData - User data.
   * @returns {IAppcuesUserProperties} AppCues identify payload.
   */
  private _buildUserProperties(userData: Partial<IUser>): IAppcuesUserProperties {
    return {
      ...this._filterUserData(userData),
      ...this._getBusinessProperties(userData),
      selectedLanguage: this._selectedLanguage ?? this._translateService.currentLang,
    };
  }

  /**
   * @description
   * Registers banner events for Appcues.
   * @returns {void} Nothing.
   */
  private _registerBannerEvents(): void {
    const appCues = this._getAppcues();

    if (!appCues || this._hasBannerEventsRegistered) {
      return;
    }

    appCues.on('all', (_name: string, event: IAppcuesEvent): void => this._onBannerBroadcast(event));
    this._hasBannerEventsRegistered = true;
  }

  private _filterUserData(initialData: Partial<IUser>): Partial<IUser> {
    const allowedKeys = this._userDataSentToAppcues;
    const filteredData: Record<string, unknown> = {};

    for (const key of allowedKeys) {
      const value = initialData[key as keyof IUser];

      if (value !== undefined) {
        filteredData[key] = value;
      }
    }

    return filteredData as Partial<IUser>;
  }

  /**
   * @description
   * Get business-related properties for Appcues
   * Includes business unit properties and food cert menu visibility
   * All properties use boolean type with default value false
   * @param {Partial<IUser>} userData - User data
   * @returns {IBusinessProperties} Business properties
   */
  private _getBusinessProperties(userData: Partial<IUser>): IBusinessProperties {
    const properties: IBusinessProperties = {
      isFoodCertMenu: false,
      isMyQimaCPUser: false,
      isMyQimaLSUser: false,
      isMyQimaProduceUser: false,
    };

    // If user is not authenticated (no id), return default false values
    if (!userData?.id) {
      return properties;
    }

    const businessUnit = this._appUserSettingService.getBusinessUnit();

    // Determine business unit type
    if (businessUnit && this._lifeScienceBusinessUnits.includes(businessUnit)) {
      properties.isMyQimaLSUser = true;
    } else if (businessUnit === EBusinessUnit.QIMA_PRODUCE) {
      properties.isMyQimaProduceUser = true;
    } else if (businessUnit === EBusinessUnit.QIMA) {
      properties.isMyQimaCPUser = true;
    }

    // Food cert menu visibility: (WQS enabled OR BRCGS enabled) AND ACA frame available
    const isWqsOrBrcgsEnabled = this._appUserSettingService.getIsWqsEnabled() || this._appUserSettingService.getIsBRCGSEnabled();

    properties.isFoodCertMenu = isWqsOrBrcgsEnabled && this._acaMessageService.isAcaFrameAvailable;

    return properties;
  }

  private _onBannerBroadcast(event: IAppcuesEvent): void {
    const isBannerEvent = event?.experienceType === EAppcuesType.BANNER || event?.stepType === EAppcuesType.BANNER;

    if (isBannerEvent) {
      const isAppcuesBannerVisible = event.name === EAppcuesEvent.STEP_SEEN || event.name === EAppcuesEvent.EXPERIENCE_STARTED;

      this.toggleBanner(isAppcuesBannerVisible);
    }
  }
}
